/**
 * EngineClientProvider — instantiates a typed engine client once tenant
 * config has resolved into a `live` or `paused` state.
 *
 * Behaviour:
 *  - Renders inside `TenantConfigProvider`. Reads `runtimeConfig.engine`
 *    and `runtimeConfig.market.locale` via `useTenantConfig`.
 *  - When tenant config changes (e.g. retry succeeds, paused → live), the
 *    client is re-created and the dedup cache reset. Multi-tenant tabs
 *    remain safe.
 *  - In states without a usable config (`loading`, `error`, `not-found`,
 *    `archived`), the provider sets the context to `null` and renders
 *    children straight through. Consumer hooks throw a clear error if
 *    they're used while no client is available.
 */
import {
  createContext,
  useContext,
  useMemo,
  useRef,
  type ReactElement,
  type ReactNode,
} from 'react';
import { createEngineClient, type EngineClient } from './createEngineClient.js';
import { useTenantConfig, useTenantRefresh } from '../tenant/useTenantConfig.js';

const EngineClientContext = createContext<EngineClient | null>(null);

interface Props {
  children: ReactNode;
}

export function EngineClientProvider({ children }: Props): ReactElement {
  const tenant = useTenantConfig();
  const refresh = useTenantRefresh();

  // Hold the latest `refresh` in a ref so the engine client's callback
  // captures the current value without forcing the client to be
  // recreated on every render of the provider.
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  const client = useMemo<EngineClient | null>(() => {
    if (tenant.kind !== 'live' && tenant.kind !== 'paused') {
      return null;
    }
    const { engine, market, tenant: tenantInfo } = tenant.config;
    return createEngineClient({
      endpoints: engine,
      slug: tenantInfo.slug,
      locale: market.locale,
      // 401 from the engine → ask the tenant provider to re-fetch
      // `/config` so a freshly-rotated `publicApiKey` can take effect
      // without a page reload. The provider itself debounces calls.
      onAuthFailure: (): void => refreshRef.current(),
    });
    // Re-create on any meaningful config change. The runtime config object
    // is replaced wholesale on refetch, so a reference comparison covers
    // tenant switches, retry recoveries, and brand-profile updates.
  }, [tenant]);

  return (
    <EngineClientContext.Provider value={client}>{children}</EngineClientContext.Provider>
  );
}

/**
 * Read the active engine client. Throws when called outside the provider
 * tree OR before the tenant config has resolved into a state with engine
 * endpoints available. The caller's component should not have mounted in
 * those cases — `App.tsx` only mounts the live shell when tenant is ready.
 */
export function useEngineClient(): EngineClient {
  const client = useContext(EngineClientContext);
  if (client === null) {
    throw new Error(
      'useEngineClient: no engine client available. Hooks consuming the engine must render inside <EngineClientProvider> AND under a tenant in `live` or `paused` state.',
    );
  }
  return client;
}
